import {
  Text,
  Image,
  Flex,
  Show,
  Button,
  Box,
  Heading,
  useBreakpointValue,
} from "@chakra-ui/react"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "@tanstack/react-query"
import { Link as RouterLink, useLocation } from "react-router-dom"

import { ReactComponent as Logo } from "../img/gyoza_logo.svg"
import { ReactComponent as LogoutMenuIcon } from "../img/logout_menu_icon.svg"
import { ReactComponent as ClosedNavbarLogo } from "../img/closed_navbar_logo.svg"
import { logout } from "../services/api"
import { useAuthContext } from "../services/auth"
import { COLORS } from "../constants/constants"

const MenuItem = ({ children, isActive, isOpen, isUserPresent, ...props }) => {
  return (
    <Flex
      as={RouterLink}
      cursor="pointer"
      width={[isUserPresent ? "32px" : "44px", "44px", "44px", "100%"]}
      alignItems="center"
      bg={isActive ? COLORS.grey : "none"}
      p={[isUserPresent ? "6px" : 3, 3, 3, isOpen ? 3 : 4]}
      height={{ base: "44px", lg: "100%" }}
      borderRadius={12}
      fontSize={14}
      color={isActive ? COLORS.red : "#6A6E85"}
      gap={3}
      justifyContent={isOpen ? "flex-start" : "center"}
      {...props}
    >
      {children}
    </Flex>
  )
}

const Menu = ({ isOpen, handleToggleMenu }) => {
  const mt = useBreakpointValue({ base: 0, lg: isOpen ? 0 : 26 })
  const mb = useBreakpointValue({ base: 0, lg: isOpen ? 24 : 34 })
  const isDesktop = useBreakpointValue({ base: false, lg: true })
  const queryClient = useQueryClient()
  const location = useLocation()

  const { t } = useTranslation()
  const { user } = useAuthContext()

  const handleOnLogout = () => {
    logout()
    queryClient.resetQueries()
  }

  const isUserPresent = !!user
  const showLoginButton = !isUserPresent && location.pathname !== "/login"

  return (
    <Flex
      position="relative"
      direction="column"
      bg="white"
      px={4}
      py={{ base: 4, lg: isOpen ? 7 : 8 }}
      boxShadow={{
        base: "0px 2px 8px rgba(20, 23, 37, 0.08)",
        lg: "10px 0 12px -5px rgba(20, 23, 37, 6%)",
      }}
      alignItems={{ base: "center", lg: isOpen ? "flex-start" : "center" }}
      zIndex={1}
      spacing={0}
    >
      <Flex
        direction={{ base: "row", lg: "column" }}
        justifyContent={{ base: "space-between", lg: "flex-start" }}
        flexBasis={{ base: 0, lg: `${isOpen ? "260px" : "88px"}` }}
        alignItems={{ base: "center", lg: isOpen ? "flex-start" : "center" }}
        flexShrink={0}
        width="100%"
        gap={{ base: 3, lg: 0 }}
        mr={{ base: 0, lg: isOpen ? "46px" : 0 }}
      >
        <Flex
          display={{ base: "none", lg: "flex" }}
          zIndex={1}
          border="1px solid #F1F1F4"
          borderRadius={8}
        >
          <button
            onClick={handleToggleMenu}
            style={{
              width: "28px",
              height: "28px",
              backgroundColor: COLORS.grey,
              position: isOpen ? "absolute" : "static",
              top: "30px",
              right: "-14px",
              borderRadius: "8px",
              padding: "0",
            }}
          >
            {isOpen ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="7"
                height="11"
                viewBox="0 0 7 11"
                fill="#6A6E85"
                style={{ marginLeft: "9px" }}
              >
                <path d="M6.84101 8.85768L3.02435 5.04102L6.84101 1.21602L5.66602 0.0410156L0.666016 5.04102L5.66602 10.041L6.84101 8.85768Z" />
              </svg>
            ) : (
              <svg
                width="7"
                height="11"
                viewBox="0 0 7 11"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginLeft: "11px" }}
              >
                <path
                  d="M0.158984 1.22435L3.97565 5.04102L0.158985 8.86602L1.33398 10.041L6.33398 5.04102L1.33398 0.0410161L0.158984 1.22435Z"
                  fill="#6A6E85"
                />
              </svg>
            )}
          </button>
        </Flex>

        <Box
          as={RouterLink}
          to="/"
          style={{ marginTop: mt, marginBottom: mb }}
          flex="0 1 auto"
        >
          {isOpen || !isDesktop ? (
            <Image
              as={Logo}
              width="100%"
              height={{ base: 9, lg: "49px" }}
              display="block"
              alt="gyoza-reward-flights"
            />
          ) : (
            <Image
              as={ClosedNavbarLogo}
              display="block"
              width="100%"
              alt="gyoza-reward-flights"
            />
          )}
        </Box>

        <Flex
          flexDirection={{ base: "row", lg: "column" }}
          gap={{ base: 2, md: 4, lg: isOpen ? 8 : 1 }}
          alignItems={{ base: "center", lg: "flex-start" }}
        >
          <Flex flexDirection={{ base: "row", lg: "column" }} gap={1}>
            <MenuItem
              to={"/"}
              isActive={
                location.pathname === "/" ||
                location.pathname.includes("/reward-flights/")
              }
              isOpen={isOpen}
              isUserPresent={isUserPresent}
            >
              <Box
                width={{ base: "100%", lg: isOpen ? 5 : 6 }}
                height={{ base: "auto", lg: isOpen ? 5 : 6 }}
                display="inline"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.838 8.38793C17.9825 8.08847 18.0486 7.75718 18.0299 7.42519C18.0112 7.09319 17.9085 6.77139 17.7313 6.49002C17.5541 6.20864 17.3083 5.97693 17.0169 5.81666C16.7256 5.65638 16.3983 5.57279 16.0658 5.57375H12.0731L10.1958 1.30666C10.1093 1.1094 9.98465 0.931138 9.82914 0.782067C9.67363 0.632996 9.49027 0.516038 9.28953 0.437878C9.08879 0.359718 8.87461 0.32189 8.65924 0.326557C8.44387 0.331223 8.23153 0.378292 8.03437 0.465073C7.8372 0.551854 7.65907 0.676644 7.51017 0.832311C7.36126 0.987977 7.24449 1.17147 7.16655 1.37229C7.0886 1.57311 7.05099 1.78733 7.05589 2.00269C7.06078 2.21805 7.10807 2.43034 7.19506 2.62742L8.49117 5.57375H4.56249L4.09566 4.5133C3.99189 4.2765 3.84244 4.06247 3.65588 3.88347C3.46932 3.70448 3.24929 3.56401 3.0084 3.47012C2.7675 3.37623 2.51046 3.33075 2.25197 3.33629C1.99348 3.34183 1.73862 3.39827 1.50197 3.5024C1.26532 3.60652 1.05151 3.75628 0.872787 3.9431C0.694061 4.12993 0.553922 4.35015 0.460384 4.59119C0.366847 4.83222 0.321747 5.08933 0.327664 5.34781C0.333581 5.60629 0.390398 5.86107 0.494867 6.09757L1.47847 8.33309C1.63214 8.68257 1.8842 8.97976 2.2039 9.18842C2.5236 9.39708 2.89712 9.50819 3.27888 9.50818H8.49117L7.19506 12.4542C7.10807 12.6513 7.06078 12.8636 7.05589 13.0789C7.05099 13.2943 7.0886 13.5085 7.16655 13.7093C7.24449 13.9101 7.36126 14.0936 7.51017 14.2493C7.65907 14.405 7.8372 14.5298 8.03437 14.6165C8.23153 14.7033 8.44387 14.7504 8.65924 14.7551C8.87461 14.7597 9.08879 14.7219 9.28953 14.6437C9.49027 14.5656 9.67363 14.4486 9.82914 14.2995C9.98465 14.1505 10.1093 13.9722 10.1958 13.775L10.2919 13.5565C10.4706 14.3377 10.8376 15.0633 11.3608 15.6702C11.8841 16.2772 12.5478 16.747 13.2942 17.0388C14.0405 17.3306 14.8469 17.4355 15.643 17.3444C16.4392 17.2533 17.201 16.969 17.8622 16.5161C18.5233 16.0633 19.0637 15.4557 19.4364 14.7462C19.809 14.0368 20.0026 13.247 20.0002 12.4456C19.9978 11.6443 19.7995 10.8557 19.4227 10.1484C19.0458 9.44122 18.5018 8.83683 17.838 8.38793ZM9.5958 13.5108C9.54377 13.629 9.46896 13.7359 9.37564 13.8252C9.28232 13.9145 9.17231 13.9846 9.05192 14.0314C8.93152 14.0782 8.80308 14.1009 8.67393 14.098C8.54478 14.0952 8.41745 14.067 8.29921 14.0149C8.18098 13.9629 8.07415 13.8881 7.98482 13.7948C7.8955 13.7014 7.82543 13.5914 7.77862 13.471C7.7318 13.3506 7.70916 13.2222 7.71199 13.0931C7.71481 12.9639 7.74305 12.8366 7.79508 12.7183L9.29355 9.31222C9.31558 9.26233 9.3248 9.20774 9.32037 9.15338C9.31595 9.09903 9.29803 9.04664 9.26823 9.00097C9.23843 8.9553 9.19769 8.9178 9.14972 8.89186C9.10176 8.86592 9.04807 8.85237 8.99354 8.85244H3.27888C3.02439 8.85242 2.77541 8.77836 2.56227 8.63931C2.34913 8.50025 2.18105 8.30219 2.07851 8.06927L1.09491 5.83374C0.954791 5.51539 0.946882 5.15441 1.07292 4.83022C1.19896 4.50603 1.44861 4.24518 1.76697 4.10507C2.08533 3.96495 2.44631 3.95704 2.7705 4.08308C3.09469 4.20912 3.35553 4.45878 3.49565 4.77713L4.04861 6.03353C4.07417 6.09181 4.11616 6.14138 4.16944 6.17618C4.22272 6.21098 4.28498 6.22951 4.34862 6.22949H8.99354C9.04807 6.22956 9.10176 6.21601 9.14973 6.19007C9.1977 6.16413 9.23843 6.12663 9.26823 6.08096C9.29803 6.03528 9.31595 5.9829 9.32037 5.92854C9.32479 5.87419 9.31557 5.81959 9.29354 5.76971L7.79508 2.36326C7.69 2.12447 7.68407 1.85372 7.77862 1.61056C7.87316 1.3674 8.06042 1.17176 8.29921 1.06667C8.538 0.961586 8.80876 0.955663 9.05192 1.05021C9.29507 1.14475 9.49072 1.33201 9.5958 1.5708L11.5592 6.03353C11.5847 6.0918 11.6267 6.14137 11.68 6.17617C11.7333 6.21097 11.7955 6.2295 11.8592 6.22948H16.0658C16.2832 6.22888 16.4973 6.2825 16.6888 6.38547C16.8803 6.48845 17.0431 6.63754 17.1625 6.81926C17.2819 7.00098 17.354 7.20959 17.3725 7.42623C17.391 7.64287 17.3552 7.86069 17.2683 8.06C16.5403 7.69535 15.7329 7.51779 14.919 7.54337C14.1052 7.56895 13.3106 7.79686 12.6069 8.20652C11.9032 8.61617 11.3126 9.19466 10.8886 9.88974C10.4645 10.5848 10.2202 11.3746 10.1779 12.1877L9.5958 13.5108ZM15.0822 16.7213C14.2392 16.7213 13.4151 16.4713 12.7142 16.003C12.0132 15.5346 11.4669 14.8689 11.1443 14.0901C10.8217 13.3113 10.7373 12.4543 10.9018 11.6275C11.0662 10.8007 11.4722 10.0412 12.0683 9.4451C12.6644 8.84901 13.4238 8.44306 14.2506 8.2786C15.0774 8.11414 15.9344 8.19855 16.7133 8.52115C17.4921 8.84375 18.1578 9.39006 18.6261 10.091C19.0945 10.7919 19.3445 11.616 19.3445 12.459C19.3432 13.589 18.8937 14.6724 18.0946 15.4715C17.2956 16.2705 16.2122 16.72 15.0822 16.7213Z"
                    fill={
                      location.pathname === "/" ||
                      location.pathname.includes("/reward-flights/")
                        ? "#DD0000"
                        : "#6A6E85"
                    }
                  />
                  <path
                    d="M17.838 8.38793C17.9825 8.08847 18.0486 7.75718 18.0299 7.42519C18.0112 7.09319 17.9085 6.77139 17.7313 6.49002C17.5541 6.20864 17.3083 5.97693 17.0169 5.81666C16.7256 5.65638 16.3983 5.57279 16.0658 5.57375H12.0731L10.1958 1.30666C10.1093 1.1094 9.98465 0.931138 9.82914 0.782067C9.67363 0.632996 9.49027 0.516038 9.28953 0.437878C9.08879 0.359718 8.87461 0.32189 8.65924 0.326557C8.44387 0.331223 8.23153 0.378292 8.03437 0.465073C7.8372 0.551854 7.65907 0.676644 7.51017 0.832311C7.36126 0.987977 7.24449 1.17147 7.16655 1.37229C7.0886 1.57311 7.05099 1.78733 7.05589 2.00269C7.06078 2.21805 7.10807 2.43034 7.19506 2.62742L8.49117 5.57375H4.56249L4.09566 4.5133C3.99189 4.2765 3.84244 4.06247 3.65588 3.88347C3.46932 3.70448 3.24929 3.56401 3.0084 3.47012C2.7675 3.37623 2.51046 3.33075 2.25197 3.33629C1.99348 3.34183 1.73862 3.39827 1.50197 3.5024C1.26532 3.60652 1.05151 3.75628 0.872787 3.9431C0.694061 4.12993 0.553922 4.35015 0.460384 4.59119C0.366847 4.83222 0.321747 5.08933 0.327664 5.34781C0.333581 5.60629 0.390398 5.86107 0.494867 6.09757L1.47847 8.33309C1.63214 8.68257 1.8842 8.97976 2.2039 9.18842C2.5236 9.39708 2.89712 9.50819 3.27888 9.50818H8.49117L7.19506 12.4542C7.10807 12.6513 7.06078 12.8636 7.05589 13.0789C7.05099 13.2943 7.0886 13.5085 7.16655 13.7093C7.24449 13.9101 7.36126 14.0936 7.51017 14.2493C7.65907 14.405 7.8372 14.5298 8.03437 14.6165C8.23153 14.7033 8.44387 14.7504 8.65924 14.7551C8.87461 14.7597 9.08879 14.7219 9.28953 14.6437C9.49027 14.5656 9.67363 14.4486 9.82914 14.2995C9.98465 14.1505 10.1093 13.9722 10.1958 13.775L10.2919 13.5565C10.4706 14.3377 10.8376 15.0633 11.3608 15.6702C11.8841 16.2772 12.5478 16.747 13.2942 17.0388C14.0405 17.3306 14.8469 17.4355 15.643 17.3444C16.4392 17.2533 17.201 16.969 17.8622 16.5161C18.5233 16.0633 19.0637 15.4557 19.4364 14.7462C19.809 14.0368 20.0026 13.247 20.0002 12.4456C19.9978 11.6443 19.7995 10.8557 19.4227 10.1484C19.0458 9.44122 18.5018 8.83683 17.838 8.38793ZM9.5958 13.5108C9.54377 13.629 9.46896 13.7359 9.37564 13.8252C9.28232 13.9145 9.17231 13.9846 9.05192 14.0314C8.93152 14.0782 8.80308 14.1009 8.67393 14.098C8.54478 14.0952 8.41745 14.067 8.29921 14.0149C8.18098 13.9629 8.07415 13.8881 7.98482 13.7948C7.8955 13.7014 7.82543 13.5914 7.77862 13.471C7.7318 13.3506 7.70916 13.2222 7.71199 13.0931C7.71481 12.9639 7.74305 12.8366 7.79508 12.7183L9.29355 9.31222C9.31558 9.26233 9.3248 9.20774 9.32037 9.15338C9.31595 9.09903 9.29803 9.04664 9.26823 9.00097C9.23843 8.9553 9.19769 8.9178 9.14972 8.89186C9.10176 8.86592 9.04807 8.85237 8.99354 8.85244H3.27888C3.02439 8.85242 2.77541 8.77836 2.56227 8.63931C2.34913 8.50025 2.18105 8.30219 2.07851 8.06927L1.09491 5.83374C0.954791 5.51539 0.946882 5.15441 1.07292 4.83022C1.19896 4.50603 1.44861 4.24518 1.76697 4.10507C2.08533 3.96495 2.44631 3.95704 2.7705 4.08308C3.09469 4.20912 3.35553 4.45878 3.49565 4.77713L4.04861 6.03353C4.07417 6.09181 4.11616 6.14138 4.16944 6.17618C4.22272 6.21098 4.28498 6.22951 4.34862 6.22949H8.99354C9.04807 6.22956 9.10176 6.21601 9.14973 6.19007C9.1977 6.16413 9.23843 6.12663 9.26823 6.08096C9.29803 6.03528 9.31595 5.9829 9.32037 5.92854C9.32479 5.87419 9.31557 5.81959 9.29354 5.76971L7.79508 2.36326C7.69 2.12447 7.68407 1.85372 7.77862 1.61056C7.87316 1.3674 8.06042 1.17176 8.29921 1.06667C8.538 0.961586 8.80876 0.955663 9.05192 1.05021C9.29507 1.14475 9.49072 1.33201 9.5958 1.5708L11.5592 6.03353C11.5847 6.0918 11.6267 6.14137 11.68 6.17617C11.7333 6.21097 11.7955 6.2295 11.8592 6.22948H16.0658C16.2832 6.22888 16.4973 6.2825 16.6888 6.38547C16.8803 6.48845 17.0431 6.63754 17.1625 6.81926C17.2819 7.00098 17.354 7.20959 17.3725 7.42623C17.391 7.64287 17.3552 7.86069 17.2683 8.06C16.5403 7.69535 15.7329 7.51779 14.919 7.54337C14.1052 7.56895 13.3106 7.79686 12.6069 8.20652C11.9032 8.61617 11.3126 9.19466 10.8886 9.88974C10.4645 10.5848 10.2202 11.3746 10.1779 12.1877L9.5958 13.5108ZM15.0822 16.7213C14.2392 16.7213 13.4151 16.4713 12.7142 16.003C12.0132 15.5346 11.4669 14.8689 11.1443 14.0901C10.8217 13.3113 10.7373 12.4543 10.9018 11.6275C11.0662 10.8007 11.4722 10.0412 12.0683 9.4451C12.6644 8.84901 13.4238 8.44306 14.2506 8.2786C15.0774 8.11414 15.9344 8.19855 16.7133 8.52115C17.4921 8.84375 18.1578 9.39006 18.6261 10.091C19.0945 10.7919 19.3445 11.616 19.3445 12.459C19.3432 13.589 18.8937 14.6724 18.0946 15.4715C17.2956 16.2705 16.2122 16.72 15.0822 16.7213Z"
                    stroke={
                      location.pathname === "/" ||
                      location.pathname.includes("/reward-flights/")
                        ? "#DD0000"
                        : "#6A6E85"
                    }
                    strokeWidth="0.4"
                  />
                  <path
                    d="M18.0349 20.0911H18.0349C18.2728 20.0908 18.5068 20.0303 18.7151 19.9153C18.9234 19.8003 19.0992 19.6345 19.2262 19.4333C19.3533 19.2321 19.4273 19.002 19.4416 18.7645C19.4559 18.527 19.4099 18.2898 19.3079 18.0748L18.2705 15.8878L18.2214 15.7843L18.1255 15.8469L17.5714 16.2086L17.4983 16.2564L17.5357 16.3352L18.5348 18.4417C18.5661 18.5076 18.5841 18.5791 18.5877 18.652C18.5914 18.7249 18.5807 18.7978 18.5561 18.8665C18.5316 18.9352 18.4938 18.9985 18.4448 19.0526C18.3959 19.1067 18.3368 19.1507 18.2708 19.1819C18.2049 19.2132 18.1334 19.2312 18.0605 19.2348C17.9876 19.2385 17.9147 19.2278 17.846 19.2032C17.7773 19.1787 17.714 19.1409 17.6599 19.092C17.6058 19.043 17.5618 18.9839 17.5305 18.9179L17.5305 18.9179L16.5483 16.8474L16.511 16.7685L16.4278 16.7949L15.7969 16.9949L15.6877 17.0295L15.7368 17.133L16.7575 19.2847L16.7575 19.2848C16.8723 19.5263 17.0533 19.7303 17.2794 19.873C17.5055 20.0157 17.7675 20.0914 18.0349 20.0911Z"
                    fill={
                      location.pathname === "/" ||
                      location.pathname.includes("/reward-flights/")
                        ? "#DD0000"
                        : "#6A6E85"
                    }
                  />
                </svg>
              </Box>

              <Show above="lg">
                <Text as="h3" display={isOpen ? "block" : "none"}>
                  {t("allReward.navbarTitle")}
                </Text>
              </Show>
            </MenuItem>

            <MenuItem
              to={"/qantas-map-explorer"}
              ml={0}
              isActive={location.pathname.includes("/qantas-map-explorer")}
              isOpen={isOpen}
              isUserPresent={isUserPresent}
            >
              <Box
                width={{ base: "100%", lg: isOpen ? 5 : 6 }}
                height={{ base: "auto", lg: isOpen ? 5 : 6 }}
              >
                <svg
                  display="inline"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12.4296 9.75616C12.5521 9.25101 12.5141 8.72024 12.3209 8.23769C12.1276 7.75515 11.7888 7.34483 11.3515 7.06382C10.9143 6.78281 10.4003 6.64509 9.88105 6.66982C9.36185 6.69456 8.86327 6.88051 8.45467 7.20181C8.04608 7.5231 7.74779 7.96377 7.60132 8.4625C7.45484 8.96122 7.46746 9.4932 7.63742 9.98442C7.80737 10.4756 8.12622 10.9017 8.54959 11.2032C8.97296 11.5048 9.47979 11.6669 9.99959 11.667"
                    stroke={
                      location.pathname.includes("/qantas-map-explorer")
                        ? "#DD0000"
                        : "#6A6E85"
                    }
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9.9923 17.9041C9.55332 17.902 9.13288 17.7268 8.8223 17.4165L5.28563 13.8807C4.50278 13.0979 3.92721 12.1324 3.6109 11.0714C3.29458 10.0104 3.24748 8.88736 3.47384 7.80362C3.7002 6.71988 4.19289 5.70958 4.90745 4.86392C5.622 4.01826 6.53591 3.36387 7.56668 2.95982C8.59744 2.55577 9.7126 2.4148 10.8115 2.54961C11.9104 2.68443 12.9584 3.0908 13.8609 3.73203C14.7634 4.37326 15.4921 5.22917 15.981 6.22248C16.4699 7.21579 16.7038 8.31523 16.6615 9.42155"
                    stroke={
                      location.pathname.includes("/qantas-map-explorer")
                        ? "#DD0000"
                        : "#6A6E85"
                    }
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M16.8333 16.8333L18.3333 18.3333M12.5 15C12.5 15.663 12.7634 16.2989 13.2322 16.7678C13.7011 17.2366 14.337 17.5 15 17.5C15.663 17.5 16.2989 17.2366 16.7678 16.7678C17.2366 16.2989 17.5 15.663 17.5 15C17.5 14.337 17.2366 13.7011 16.7678 13.2322C16.2989 12.7634 15.663 12.5 15 12.5C14.337 12.5 13.7011 12.7634 13.2322 13.2322C12.7634 13.7011 12.5 14.337 12.5 15Z"
                    stroke={
                      location.pathname.includes("/qantas-map-explorer")
                        ? "#DD0000"
                        : "#6A6E85"
                    }
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Box>

              <Show above="lg">
                <Text as="h3" display={isOpen ? "block" : "none"}>
                  {t("mapView.title")}
                </Text>
              </Show>
            </MenuItem>

            <MenuItem
              to={"/all-reward-flights"}
              ml={0}
              isActive={location.pathname.startsWith("/all-reward-flights")}
              isOpen={isOpen}
              isUserPresent={isUserPresent}
            >
              <Box
                width={{ base: "100%", lg: isOpen ? 5 : 6 }}
                height={{ base: "auto", lg: isOpen ? 5 : 6 }}
                display="inline"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.25 16.25H16.25V17.5H11.25V16.25ZM11.25 13.75H18.75V15H11.25V13.75ZM11.25 11.25H18.75V12.5H11.25V11.25Z"
                    fill={
                      location.pathname.startsWith("/all-reward-flights")
                        ? "#DD0000"
                        : "#6A6E85"
                    }
                  />
                  <path
                    d="M12.8429 7.01062L10.4482 2.15846C10.2648 1.78684 9.73485 1.78684 9.55144 2.15846L7.15668 7.01062L1.80231 7.7887C1.39221 7.84829 1.22845 8.35225 1.52519 8.64152L5.39981 12.4188L4.48487 17.7515C4.41479 18.1599 4.84348 18.4714 5.21031 18.2786L8.7498 16.4181V15.0056L5.97418 16.465L6.63168 12.63L6.74293 11.9819L6.27231 11.5238L3.48605 8.80687L7.33668 8.2475L7.98668 8.15312L8.27793 7.56375L9.9998 4.07438L11.7217 7.56375L12.0129 8.15312L12.6629 8.2475L17.3217 8.92562L17.4998 7.6875L12.8429 7.01062Z"
                    fill={
                      location.pathname.startsWith("/all-reward-flights")
                        ? "#DD0000"
                        : "#6A6E85"
                    }
                  />
                </svg>
              </Box>
              <Show above="lg">
                <Text as="h3" display={isOpen ? "block" : "none"}>
                  {t("allBusinessFirstClasses")}
                </Text>
              </Show>
            </MenuItem>

            <MenuItem
              to={"/flight-availability"}
              ml={0}
              isActive={location.pathname.includes("/flight-availability")}
              isOpen={isOpen}
              isUserPresent={isUserPresent}
            >
              <Box
                width={{ base: "100%", lg: isOpen ? 5 : 6 }}
                height={{ base: "auto", lg: isOpen ? 5 : 6 }}
                display="inline"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.99757 12.5568L7.99758 12.5568C8.07589 12.7916 8.29564 12.95 8.54312 12.95H16.1431C16.8749 12.95 17.4681 13.5432 17.4681 14.275C17.4681 15.0068 16.8749 15.6 16.1431 15.6H5.19205L2.09069 3.19455C1.88185 2.35919 2.51365 1.55 3.37472 1.55C3.9444 1.55 4.45018 1.91454 4.63034 2.455L7.99757 12.5568ZM3.37472 0.4C1.7655 0.4 0.584727 1.91229 0.975025 3.47346L4.18527 16.3145C4.24926 16.5705 4.47927 16.75 4.7431 16.75H16.1431C17.51 16.75 18.6181 15.6419 18.6181 14.275C18.6181 12.9081 17.51 11.8 16.1431 11.8H8.95758L5.72132 2.09134C5.38463 1.08129 4.4394 0.4 3.37472 0.4Z"
                    fill={
                      location.pathname.includes("/flight-availability")
                        ? "#DD0000"
                        : "#6A6E85"
                    }
                    stroke={
                      location.pathname.includes("/flight-availability")
                        ? "#DD0000"
                        : "#6A6E85"
                    }
                    strokeWidth="0.2"
                  />
                  <path
                    d="M16.1434 19.5998H16.2434V19.4998V18.5498V18.4498H16.1434H5.69336H5.59336V18.5498V19.4998V19.5998H5.69336H16.1434Z"
                    fill={
                      location.pathname.includes("/flight-availability")
                        ? "#DD0000"
                        : "#6A6E85"
                    }
                    stroke={
                      location.pathname.includes("/flight-availability")
                        ? "#DD0000"
                        : "#6A6E85"
                    }
                    strokeWidth="0.2"
                  />
                  <path
                    d="M12.8187 9.14961H12.9187V9.04961V8.09961V7.99961H12.8187H7.59375H7.49375V8.09961V9.04961V9.14961H7.59375H12.8187Z"
                    fill={
                      location.pathname.includes("/flight-availability")
                        ? "#DD0000"
                        : "#6A6E85"
                    }
                    stroke={
                      location.pathname.includes("/flight-availability")
                        ? "#DD0000"
                        : "#6A6E85"
                    }
                    strokeWidth="0.2"
                  />
                </svg>
              </Box>
              <Show above="lg">
                <Text as="h3" display={isOpen ? "block" : "none"}>
                  {t("amadeus.navbarTitle")}
                </Text>
              </Show>
            </MenuItem>

            <MenuItem
              to={"/pricing"}
              ml={0}
              isActive={location.pathname === "/pricing"}
              isOpen={isOpen}
              isUserPresent={isUserPresent}
            >
              <Box
                width={{ base: "100%", lg: isOpen ? 5 : 6 }}
                height={{ base: "auto", lg: isOpen ? 5 : 6 }}
                display="inline"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15.7535 1.25H10.9516C10.8023 1.24989 10.659 1.30888 10.5531 1.41406L0.953127 11.0117C0.742976 11.2229 0.625 11.5087 0.625 11.8066C0.625 12.1046 0.742976 12.3904 0.953127 12.6016L5.52344 17.1719C5.73469 17.3821 6.02056 17.5 6.31856 17.5C6.61655 17.5 6.90242 17.3821 7.11367 17.1719L16.7109 7.57813C16.8161 7.47221 16.8751 7.32896 16.875 7.17969V2.375C16.8757 2.22736 16.8472 2.08103 16.7912 1.94444C16.7351 1.80786 16.6526 1.68371 16.5484 1.57914C16.4441 1.47458 16.3202 1.39167 16.1838 1.33518C16.0474 1.27869 15.9012 1.24974 15.7535 1.25Z"
                    stroke={
                      location.pathname === "/pricing" ? "#DD0000" : "#6A6E85"
                    }
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13.75 5.625C13.5028 5.625 13.2611 5.55169 13.0555 5.41434C12.85 5.27699 12.6898 5.08176 12.5952 4.85336C12.5005 4.62495 12.4758 4.37361 12.524 4.13114C12.5723 3.88866 12.6913 3.66593 12.8661 3.49112C13.0409 3.3163 13.2637 3.19725 13.5061 3.14902C13.7486 3.10079 13.9999 3.12554 14.2284 3.22015C14.4568 3.31476 14.652 3.47498 14.7893 3.68054C14.9267 3.8861 15 4.12777 15 4.375C15 4.70652 14.8683 5.02446 14.6339 5.25888C14.3995 5.4933 14.0815 5.625 13.75 5.625Z"
                    fill={
                      location.pathname === "/pricing" ? "#DD0000" : "#6A6E85"
                    }
                  />
                  <path
                    d="M8.98438 18.75L19.2188 8.51562C19.2696 8.4643 19.3097 8.40328 19.3365 8.33617C19.3634 8.26907 19.3765 8.19726 19.375 8.125V3.125"
                    stroke={
                      location.pathname === "/pricing" ? "#DD0000" : "#6A6E85"
                    }
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Box>

              <Show above="lg">
                <Text as="h3" display={isOpen ? "block" : "none"}>
                  {t("prices")}
                </Text>
              </Show>
            </MenuItem>

            {isUserPresent && (
              <>
                <MenuItem
                  to={"/settings"}
                  ml={0}
                  isActive={location.pathname === "/settings"}
                  isOpen={isOpen}
                  isUserPresent={isUserPresent}
                >
                  <Box
                    width={{ base: "100%", lg: isOpen ? 5 : 6 }}
                    height={{ base: "auto", lg: isOpen ? 5 : 6 }}
                    display="inline"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z"
                        stroke={
                          location.pathname === "/settings"
                            ? "#DD0000"
                            : "#6A6E85"
                        }
                        strokeWidth="1.5"
                      />
                      <path
                        d="M11.4712 1.79366C11.1654 1.66699 10.777 1.66699 10.0004 1.66699C9.22371 1.66699 8.83537 1.66699 8.52954 1.79366C8.32719 1.87742 8.14333 2.00024 7.98848 2.15509C7.83362 2.30995 7.7108 2.49381 7.62704 2.69616C7.55038 2.88199 7.51954 3.09949 7.50787 3.41533C7.50268 3.64365 7.43954 3.86691 7.32438 4.06413C7.20923 4.26136 7.04583 4.42608 6.84954 4.54283C6.65029 4.65453 6.42591 4.71374 6.19749 4.71491C5.96906 4.71608 5.74409 4.65915 5.54371 4.54949C5.26371 4.40116 5.06121 4.31949 4.86038 4.29283C4.42231 4.23522 3.97929 4.35391 3.62871 4.62282C3.36704 4.82532 3.17204 5.16116 2.78371 5.83366C2.39538 6.50616 2.20038 6.84199 2.15788 7.17116C2.12924 7.3882 2.14364 7.60876 2.20026 7.82024C2.25689 8.03171 2.35462 8.22996 2.48788 8.40366C2.61121 8.56366 2.78371 8.69782 3.05121 8.86616C3.44538 9.11366 3.69871 9.53532 3.69871 10.0003C3.69871 10.4653 3.44538 10.887 3.05121 11.1337C2.78371 11.3028 2.61038 11.437 2.48788 11.597C2.35462 11.7707 2.25689 11.9689 2.20026 12.1804C2.14364 12.3919 2.12924 12.6124 2.15788 12.8295C2.20121 13.1578 2.39538 13.4945 2.78288 14.167C3.17204 14.8395 3.36621 15.1753 3.62871 15.3778C3.80241 15.5111 4.00065 15.6088 4.21213 15.6654C4.42361 15.7221 4.64416 15.7365 4.86121 15.7078C5.06121 15.6812 5.26371 15.5995 5.54371 15.4512C5.74409 15.3415 5.96906 15.2846 6.19749 15.2857C6.42591 15.2869 6.65029 15.3461 6.84954 15.4578C7.25204 15.6912 7.49121 16.1203 7.50787 16.5853C7.51954 16.902 7.54954 17.1187 7.62704 17.3045C7.7108 17.5068 7.83362 17.6907 7.98848 17.8456C8.14333 18.0004 8.32719 18.1232 8.52954 18.207C8.83537 18.3337 9.22371 18.3337 10.0004 18.3337C10.777 18.3337 11.1654 18.3337 11.4712 18.207C11.6736 18.1232 11.8574 18.0004 12.0123 17.8456C12.1671 17.6907 12.2899 17.5068 12.3737 17.3045C12.4504 17.1187 12.4812 16.902 12.4929 16.5853C12.5095 16.1203 12.7487 15.6903 13.1512 15.4578C13.3505 15.3461 13.5748 15.2869 13.8033 15.2857C14.0317 15.2846 14.2567 15.3415 14.457 15.4512C14.737 15.5995 14.9395 15.6812 15.1395 15.7078C15.3566 15.7365 15.5771 15.7221 15.7886 15.6654C16.0001 15.6088 16.1983 15.5111 16.372 15.3778C16.6345 15.1762 16.8287 14.8395 17.217 14.167C17.6054 13.4945 17.8004 13.1587 17.8429 12.8295C17.8715 12.6124 17.8571 12.3919 17.8005 12.1804C17.7439 11.9689 17.6461 11.7707 17.5129 11.597C17.3895 11.437 17.217 11.3028 16.9495 11.1345C16.7543 11.0158 16.5925 10.8495 16.4792 10.6511C16.3659 10.4527 16.305 10.2288 16.302 10.0003C16.302 9.53532 16.5554 9.11366 16.9495 8.86699C17.217 8.69782 17.3904 8.56366 17.5129 8.40366C17.6461 8.22996 17.7439 8.03171 17.8005 7.82024C17.8571 7.60876 17.8715 7.3882 17.8429 7.17116C17.7995 6.84282 17.6054 6.50616 17.2179 5.83366C16.8287 5.16116 16.6345 4.82532 16.372 4.62282C16.1983 4.48957 16.0001 4.39184 15.7886 4.33521C15.5771 4.27859 15.3566 4.26419 15.1395 4.29283C14.9395 4.31949 14.737 4.40116 14.4562 4.54949C14.2559 4.659 14.0311 4.71584 13.8028 4.71468C13.5746 4.71351 13.3504 4.65438 13.1512 4.54283C12.9549 4.42608 12.7915 4.26136 12.6764 4.06413C12.5612 3.86691 12.4981 3.64365 12.4929 3.41533C12.4812 3.09866 12.4512 2.88199 12.3737 2.69616C12.2899 2.49381 12.1671 2.30995 12.0123 2.15509C11.8574 2.00024 11.6736 1.87742 11.4712 1.79366Z"
                        stroke={
                          location.pathname === "/settings"
                            ? "#DD0000"
                            : "#6A6E85"
                        }
                        strokeWidth="1.5"
                      />
                    </svg>
                  </Box>

                  <Show above="lg">
                    <Text as="h3" display={isOpen ? "block" : "none"}>
                      {t("settings.title")}
                    </Text>
                  </Show>
                </MenuItem>

                <MenuItem
                  to={"/alerts"}
                  ml={0}
                  isActive={location.pathname === "/alerts"}
                  isOpen={isOpen}
                  isUserPresent={isUserPresent}
                >
                  <Box
                    width={{ base: "100%", lg: isOpen ? 5 : 6 }}
                    height={{ base: "auto", lg: isOpen ? 5 : 6 }}
                    display="inline"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9.99967 14.1667H3.33301C3.78712 13.9095 4.17452 13.5493 4.464 13.1151C4.75348 12.6809 4.93696 12.1848 4.99967 11.6667V9.16667C5.04931 8.10923 5.38576 7.08523 5.97297 6.20442C6.56017 5.32361 7.376 4.61919 8.33301 4.16667C8.33301 3.72464 8.5086 3.30072 8.82116 2.98816C9.13372 2.67559 9.55765 2.5 9.99967 2.5C10.4417 2.5 10.8656 2.67559 11.1782 2.98816C11.4907 3.30072 11.6663 3.72464 11.6663 4.16667C12.6233 4.61919 13.4392 5.32361 14.0264 6.20442C14.6136 7.08523 14.95 8.10923 14.9997 9.16667V9.58333M15.8338 17.5C15.3918 17.5 14.9679 17.3244 14.6553 17.0118C14.3428 16.6993 14.1672 16.2754 14.1672 15.8333C14.1672 15.3913 14.3428 14.9674 14.6553 14.6548C14.9679 14.3423 15.3918 14.1667 15.8338 14.1667M15.8338 17.5C16.2759 17.5 16.6998 17.3244 17.0124 17.0118C17.3249 16.6993 17.5005 16.2754 17.5005 15.8333C17.5005 15.3913 17.3249 14.9674 17.0124 14.6548C16.6998 14.3423 16.2759 14.1667 15.8338 14.1667M15.8338 17.5V18.75M15.8338 14.1667V12.9167M18.3597 14.375L17.2772 15M14.3913 16.6667L13.308 17.2917M13.308 14.375L14.3913 15M17.2772 16.6667L18.3605 17.2917M7.49967 14.1667V15C7.49967 15.663 7.76307 16.2989 8.23191 16.7678C8.70075 17.2366 9.33663 17.5 9.99967 17.5"
                        stroke={
                          location.pathname === "/alerts"
                            ? "#DD0000"
                            : "#6A6E85"
                        }
                        strokeWidth="1.3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Box>

                  <Show above="lg">
                    <Text as="h3" display={isOpen ? "block" : "none"}>
                      {t("alerts.header")}
                    </Text>
                  </Show>
                </MenuItem>

                <MenuItem
                  ml={0}
                  onClick={handleOnLogout}
                  isOpen={isOpen}
                  isUserPresent={isUserPresent}
                >
                  <Image
                    as={LogoutMenuIcon}
                    width={{ base: "100%", lg: isOpen ? 5 : 6 }}
                    height={{ base: "100%", lg: isOpen ? 5 : 6 }}
                    display="inline"
                    stroke="#6A6E85"
                  />
                  <Show above="lg">
                    <Text as="h3" display={isOpen ? "block" : "none"}>
                      {t("logout")}
                    </Text>
                  </Show>
                </MenuItem>
              </>
            )}
          </Flex>

          {showLoginButton && (
            <Box w={{ lg: "100%" }}>
              <RouterLink to="/login">
                <Button
                  backgroundColor={COLORS.red}
                  color="white"
                  w={{ base: "44px", lg: isOpen ? "100%" : 12 }}
                  h={{ base: "44px", lg: isOpen ? "auto" : 12 }}
                  padding={{ base: 3, lg: 4 }}
                >
                  {isOpen && isDesktop ? (
                    <Heading
                      as="h3"
                      textTransform="uppercase"
                      fontSize={{ base: "12px", lg: "16px" }}
                    >
                      login
                    </Heading>
                  ) : (
                    <Image
                      as={LogoutMenuIcon}
                      width={{ base: 5, lg: 6 }}
                      height={{ base: 5, lg: 6 }}
                      stroke="#fff"
                    />
                  )}
                </Button>
              </RouterLink>
            </Box>
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}

export default Menu
